import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Render, type Data } from '@puckeditor/core';
import { buildConfig } from '@nailthelanding/blocks/config';
import tokensUrl from '@nailthelanding/blocks/tokens.css?url';
import blocksStylesUrl from '@nailthelanding/blocks/style.css?url';
import { Monitor, Tablet, Smartphone, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import styles from './TemplatePreviewPane.module.css';

type Device = 'desktop' | 'tablet' | 'mobile';

export type TemplatePreviewPaneProps = {
  templateName: string | null;
  data: Data | null;
  themeCss: string;
  customCss: string;
  loading: boolean;
  canCustomize: boolean;
  onCustomize: () => void;
  /** When true, the pane skips its own title/CTA strip. The caller is
   * responsible for rendering framing chrome (e.g. the dialog header). */
  hideHeader?: boolean;
};

const noopUpload = async (): Promise<string> => {
  throw new Error('Image upload is not available in preview mode.');
};

type DeviceToggleProps = {
  device: Device;
  onChange: (next: Device) => void;
};

const DeviceToggle = ({ device, onChange }: DeviceToggleProps) => {
  const devices: Array<{ id: Device; label: string; Icon: typeof Monitor }> = [
    { id: 'desktop', label: 'Desktop', Icon: Monitor },
    { id: 'tablet', label: 'Tablet', Icon: Tablet },
    { id: 'mobile', label: 'Mobile', Icon: Smartphone },
  ];
  return (
    <div className={styles.deviceToggle} role="group" aria-label="Preview device">
      {devices.map(({ id, label, Icon }) => (
        <Button
          key={id}
          type="button"
          size="dense"
          variant={device === id ? 'primary' : 'tertiary'}
          onClick={() => onChange(id)}
          aria-label={label}
          aria-pressed={device === id}
        >
          <Icon className="h-4 w-4 stroke-[1.5]" />
        </Button>
      ))}
    </div>
  );
};

type PreviewIframeProps = {
  data: Data;
  themeCss: string;
  customCss: string;
  className: string;
};

/**
 * Hosts the rendered template inside an iframe so that the template's
 * viewport-based media queries fire against the iframe's width — letting
 * the device toggle (desktop / tablet / mobile) actually exercise mobile
 * breakpoints. The React tree is portalled into the iframe document so
 * Puck's <Render> stays a normal React subtree (props, callbacks, etc.).
 */
const PreviewIframe = ({ data, themeCss, customCss, className }: PreviewIframeProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [body, setBody] = useState<HTMLBodyElement | null>(null);
  const config = useMemo(() => buildConfig({ upload: noopUpload }), []);

  // Initialize the iframe document once. about:blank is same-origin, so we
  // can write to its DOM directly. Subsequent CSS / data updates are made in
  // separate effects so we don't blow away the body (and the React portal).
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const init = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;
      doc.open();
      doc.write(
        `<!doctype html><html><head>
           <meta charset="utf-8" />
           <meta name="viewport" content="width=device-width, initial-scale=1" />
           <link rel="stylesheet" href="${tokensUrl}" />
           <link rel="stylesheet" href="${blocksStylesUrl}" />
           <style id="theme-css"></style>
           <style id="custom-css"></style>
           <style>
             html, body { margin: 0; padding: 0; height: 100%; }
             body { background: var(--color-bg, transparent); }
           </style>
         </head><body></body></html>`,
      );
      doc.close();
      setBody(doc.body as HTMLBodyElement);
    };
    init();
  }, []);

  // Update the per-site theme + custom CSS without recreating the iframe
  // document.
  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc || !body) return;
    const theme = doc.getElementById('theme-css');
    if (theme) theme.textContent = themeCss;
    const custom = doc.getElementById('custom-css');
    if (custom) custom.textContent = customCss;
  }, [themeCss, customCss, body]);

  return (
    <>
      <iframe ref={iframeRef} title="Template preview" className={className} />
      {body && createPortal(<Render config={config} data={data} />, body)}
    </>
  );
};

export const TemplatePreviewPane = ({
  templateName,
  data,
  themeCss,
  customCss,
  loading,
  canCustomize,
  onCustomize,
  hideHeader = false,
}: TemplatePreviewPaneProps) => {
  const [device, setDevice] = useState<Device>('desktop');

  return (
    <div className={styles.pane}>
      {!hideHeader && (
        <div className={styles.header}>
          <div className={styles.headerText}>
            <div className="text-base font-medium text-foreground">
              {templateName ?? 'Live preview'}
            </div>
            <div className="text-sm text-muted-foreground">
              {data ? 'Interactive preview · click Customize to edit' : 'Pick a template to see it here'}
            </div>
          </div>
          <div className={styles.headerActions}>
            <DeviceToggle device={device} onChange={setDevice} />
            <Button
              type="button"
              variant="primary"
              size="cta"
              onClick={onCustomize}
              disabled={!canCustomize}
            >
              Customize this
              <ArrowRight className="h-4 w-4 ml-1 stroke-[1.5]" />
            </Button>
          </div>
        </div>
      )}

      {hideHeader && (
        <div className={styles.miniDeviceBar}>
          <DeviceToggle device={device} onChange={setDevice} />
        </div>
      )}

      <div className={styles.stage}>
        {loading && <div className={styles.status}>Loading template…</div>}
        {!loading && !data && (
          <div className={styles.empty}>Select a template on the left to preview it here.</div>
        )}
        {!loading && data && (
          <div className={`${styles.frame} ${styles[`frame_${device}`]}`}>
            <PreviewIframe
              data={data}
              themeCss={themeCss}
              customCss={customCss}
              className={styles.iframe}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatePreviewPane;
